import { useTranslation } from 'next-i18next/pages';
import { unescape } from 'es-toolkit/string';
import { getUploadcareImageTransformationProps } from '@pkgs/components/utils/getUploadcareImageTransformationProps';
import { AiBadge, type TFunction } from '@pkgs/components/index';
import { ensureHttpsPrefix } from '@pkgs/api/utils/urls';
import { formatShopPhoneNumber } from '@pkgs/api/utils/strings';
import { DEFAULT_IMAGE, getUploadCareImageUrl, ImageAiType } from '@pkgs/api';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import { getParsedShopName } from '@/utils/propertyDataUtils';
import { joinWithSeparator } from '@/utils/formatters';
import { getAiBadgeTranslationKey } from '@/utils/aiBadgeUtils';
import { useExposeModal } from '@/providers/ExposeModalProvider';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { ImageComponent } from '@/components/common/ImageComponent/ImageComponent';
import { MailBoldIcon } from '@/components/common/Icons/MailBoldIcon';

import { PhoneLinkWrapper } from '../PhoneLinkWrapper/PhoneLinkWrapper';
import { Link } from '../Link';
import { ImprintModal } from '../ContactForm/components/ImprintModal';
import { BulletList } from '../BulletList/BulletList';

import styles from './ContactInfo.module.css';

export const ContactInfo = () => {
  const {
    data: { genericListing: listing },
  } = useListing();
  const { trackExposeNavigationEvent } = useExposeTracking();
  const { t } = useTranslation([TRANSLATION_NAMESPACE.property_expose, TRANSLATION_NAMESPACE.private_office]);
  const { onContactModalTriggerClick } = useExposeModal();
  const isUAExclusiveListingAndNotSignedIn = useShouldHideUserAccountExclusiveContent();
  const phoneNumbers = [listing.shopPhoneNumber, listing.agent?.mobile].filter<string>((i): i is string => !!i).map(formatShopPhoneNumber);
  const { pageVariant } = useExposePageProps();
  const prefixedShopname = `Engel & Völkers ${getParsedShopName(listing.shopName ?? '')}`;

  const liPaLabel = `${t('search.expose.shopCard.label.licencePartnerOf')} ${unescape(listing.shopMasterLicencePartner ?? '')}`;

  const imprintModalState = useModalState();

  const { imprint, shopDataSecurityUrl } = listing.profile;

  const privacyUrl = listing.privacyPolicyUrl ?? shopDataSecurityUrl;

  const privacyUrlPrefixed = privacyUrl ? ensureHttpsPrefix(privacyUrl) : undefined;

  const shopWebURL = listing.shopWebUrl ? ensureHttpsPrefix(listing.shopWebUrl) : undefined;
  const shopImageSource = listing.shopProfilePictureUploadcareId
    ? getUploadCareImageUrl({ imageId: listing.shopProfilePictureUploadcareId })
    : (listing.shopProfilePicture ?? DEFAULT_IMAGE);

  const imprintComponent = listing.imprintUrl ? (
    <Link target="_blank" rel="noreferrer" href={listing.imprintUrl} label={t('search.expose.shopCard.link.providerImprint')} />
  ) : (
    <Link label={t('search.expose.shopCard.link.providerImprint')} onClick={imprintModalState.open} as="button" />
  );

  const shopLicenseText =
    listing.shopLicenseIdLabel && listing.shopLicenseId ? `${listing.shopLicenseIdLabel}: ${listing.shopLicenseId}` : undefined;

  const reportingChannelUrl = listing.reportingChannelUrl ? ensureHttpsPrefix(listing.reportingChannelUrl) : undefined;

  if (pageVariant.includes('private-office')) {
    const officeNameParts = [listing.officeName];
    if ('shopRegistrationNumber' in listing) {
      officeNameParts.push(listing.shopRegistrationNumber);
    }
    return (
      <section className={styles.section}>
        <Wrapper as="section" width={{ tabletLandscape: 'm', laptop: 'm' }}>
          <div className={styles.containerPrivateOffice} data-test-id="expose-contact-section-private-office">
            <Overline text={t('search.expose.shopCard.providerOverline')} />
            <div className={styles.addressWrapper}>
              {!!listing.officeName && <Copy text={joinWithSeparator(officeNameParts, ', ')} />}
              <Copy text={listing.shopAddressStreet} />
              <Copy text={joinWithSeparator([listing.shopAddressCity, listing.shopAddressPostalCode, listing.shopAddressCountry], ', ')} />
              {!!shopLicenseText && <Copy className={styles.shopLicenseText} text={shopLicenseText} />}
            </div>
            <div className={styles.linksWrapper}>
              {imprintComponent}
              {privacyUrlPrefixed && <Link href={privacyUrlPrefixed} label={t('private_office:search.expose.contactInfo.privacyPolicy')} />}
              {!!reportingChannelUrl && (
                <Link
                  href={reportingChannelUrl}
                  label={t('search.expose.shopCard.label.reportingChannel')}
                  target="_blank"
                  rel="noreferrer"
                />
              )}
            </div>
          </div>
        </Wrapper>
      </section>
    );
  }
  return (
    <section className={styles.section}>
      <Wrapper as="section" width={{ tabletLandscape: 'm', laptop: 'm' }}>
        <div className={styles.container} data-testid="expose-contact-section">
          <Overline text={t('search.expose.shopCard.subtitle')} />
          <Headline className={styles.headline} size="h3" as="h2" label={prefixedShopname} />
          <div className={styles.shopWrapper}>
            <div className={styles.imageContainerWrapper}>
              <div className={styles.imageContainer}>
                <ImageComponent
                  src={shopImageSource}
                  fill
                  alt="engel & völkers shop image"
                  data-testid="expose-contact-section_shop-image"
                  style={{ objectFit: 'cover' }}
                  sizes="(max-width: 600px) 120px, 180px"
                  {...getUploadcareImageTransformationProps({ aspectRatio: '1/1', fit: 'cover' })}
                />
                {listing.shopProfilePictureAiGenerated && (
                  <AiBadge
                    aiType={ImageAiType.MODIFIED}
                    t={t as TFunction}
                    translationKey={getAiBadgeTranslationKey({ size: 's' })}
                    size="m"
                    offset="xs"
                    side="center"
                    isPrivateOffice={listing.isPrivateOffice}
                  />
                )}
              </div>
            </div>
            <div className={styles.detailsWrapper}>
              <Copy text={t('search.expose.shopCard.provider')} />
              {Boolean(listing.officeName) && (
                <Copy
                  text={joinWithSeparator(
                    [listing.officeName, ...('shopRegistrationNumber' in listing ? [listing.shopRegistrationNumber] : [])],
                    ', ',
                  )}
                />
              )}
              <BulletList
                items={[
                  !!shopWebURL && (
                    <Copy
                      key={shopWebURL}
                      text={
                        <Link
                          label={t('search.contact.form.socialContact.website')}
                          href={shopWebURL}
                          target="_blank"
                          rel="noreferrer"
                          trackingInfo={{
                            handleEvent: () => {
                              trackExposeNavigationEvent({ targetUrl: shopWebURL, elementId: 'contact-info_shop_link' });
                            },
                          }}
                        />
                      }
                    />
                  ),
                  (listing.imprintUrl || imprint) && <Copy key={imprint} text={imprintComponent} />,
                ]}
              />
              {Boolean(privacyUrlPrefixed) && (
                <Copy
                  key={privacyUrlPrefixed}
                  text={
                    <Link
                      label={t('search.expose.shopCard.label.privacyPolicy')}
                      href={privacyUrlPrefixed}
                      target="_blank"
                      rel="noreferrer"
                    />
                  }
                />
              )}
              {Boolean(reportingChannelUrl) && (
                <Copy
                  text={
                    <Link
                      href={reportingChannelUrl}
                      label={t('search.expose.shopCard.label.reportingChannel')}
                      target="_blank"
                      rel="noreferrer"
                    />
                  }
                />
              )}
              {!!shopLicenseText && <Copy text={shopLicenseText} />}
              {!!listing.shopMasterLicencePartner && <Copy className={styles.licensePartnerText} text={liPaLabel} />}
              {phoneNumbers.length > 0 && (
                <BulletList
                  items={phoneNumbers.map((phoneNumber, index) => (
                    <PhoneLinkWrapper key={phoneNumber} trigger="contact-card" index={index} phoneNumberCount={phoneNumbers.length}>
                      <Copy size="l" text={<Link href={'tel:' + phoneNumber} label={phoneNumber} />} />
                    </PhoneLinkWrapper>
                  ))}
                />
              )}
            </div>
          </div>
          {!isUAExclusiveListingAndNotSignedIn && (
            <Button
              className={styles.ctaButton}
              onClick={onContactModalTriggerClick}
              testId="contact-info_primary_request-expose"
              label={t('search.expose.shopCard.label.requestDetailsButton')}
              icon={<MailBoldIcon />}
            />
          )}
          {!!imprint && <ImprintModal imprint={imprint} modalState={imprintModalState} />}
        </div>
      </Wrapper>
    </section>
  );
};
